import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ZoomModal from "./heatmap/zoomModal";
import "./chart.css";
import { OverallParticipation } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import { API_RESPONSE } from "../utills/app-constant";
import Modal from "../admin/components/common/Model";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";
import Remark from "../admin/components/Charts/Remark";

const DonutChartWithToolbar = ({ selectedCompanyId, selectedCampaignId, graphName, type, title = "Overall Participation" }) => {
  const chartRef = useRef(null);
  const [rowData, setRowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seriesData, setSeriesData] = useState([0, 0, 0]);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState("")
  const modalRef = useRef(null);
  const modalId = `zoomModal-${title.replace(/\s+/g, "_")}`;

  /** Fetch API Data **/
  const getOverallParticipationData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OverallParticipation(selectedCompanyId, selectedCampaignId, type || "all", graphName);
      if (response?.data?.responseCode === 200) {
        const data = response.data;
        setRemarkText(response.data.remark || "");
        setRemarkId(response.data.remarkId || "");
        setRowData(data);
        setSeriesData([
          data.attemptedEmployee || 0,
          data.inProgressEmployee || 0,
          data.notAttemptedEmployee || 0,
        ]);
      } else if (response?.data?.responseCode === 404) {
        setRowData(null);
        setSeriesData([0, 0, 0]);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, graphName, type, graphName]);

  useEffect(() => {
    if (!selectedCompanyId || !selectedCampaignId) return;
    getOverallParticipationData(selectedCompanyId, selectedCampaignId, type, graphName);
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);


  /** Chart Options **/
  const chartOptions = useMemo(
    () => ({
      chart: { id: "overall_participation", type: "donut", toolbar: { show: false } },
      labels: ["Completed", "In Progress", "Not Started"],
      colors: ["#28a745", "#ffc107", "#dc3545"],
      legend: { position: "top", offsetY: 0 },
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total",
                formatter: () => rowData?.totalEmployee || 0,
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => `${opts.w.config.series[opts.seriesIndex] || 0}`,
        style: { fontSize: "10px" },
      },
      tooltip: { enabled: true, y: { formatter: val => `${val} participants` } },
    }),
    [rowData]
  );

  /** Common helper **/
  const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };

  /** Image Export **/
  const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  /** PDF Export **/
  const exportAsPDF = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
    const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF

    const canvasWidth = canvas.width; // Width of the canvas image
    const canvasHeight = canvas.height; // Height of the canvas image

    // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
    const scaleFactor = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%

    const imgWidth = canvasWidth * scaleFactor; // Scaled width
    const imgHeight = canvasHeight * scaleFactor; // Scaled height

    // Calculate the offset to center the image
    const xOffset = (pdfWidth - imgWidth) / 2;
    const yOffset = (pdfHeight - imgHeight) / 2;

    // Add the image to the PDF, centered and scaled down
    pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);

    // Save the PDF
    pdf.save(`${title || "chart"}.pdf`);
  };


  /** Excel Export **/
  const exportSummaryToExcel = () => {
    const summaryRows = [
      [`Total Employees = ${rowData?.totalEmployee || 0}`],
      [`Completed = ${rowData?.attemptedEmployee || 0}`],
      [`In Progress = ${rowData?.inProgressEmployee || 0}`],
      [`Not Started = ${rowData?.notAttemptedEmployee || 0}`],
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(summaryRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${title}.xlsx`
    );
  };

  const totalEmployees = rowData?.totalEmployee || 0;
  const completedEmployees = rowData?.attemptedEmployee || 0;
  const completionPercentage = rowData?.completionPercentage || 0;

  return (
    <div id={`chart-box-${title}`} className="card border h-100 position-relative">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center overall-text p-2">
        <h5 className="fw-semibold m-0">{title}</h5>
        <div className="d-flex">
          <button title="Zoom In" data-bs-toggle="modal" data-bs-target={`#${modalId}`} className="border-0 bg-transparent me-3">
            {/* <i className="bi bi-zoom-in"></i> */}
            <img src={ZOOM_ICON} alt="zoom" className="" />
          </button>

          <div className="dropdown">
            <button className="icon-btn bg-transparent" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              {/* <i className="bi bi-three-dots"></i> */}
              <img src={MENU_ICON} alt="menu" className="" />
            </button>
            <ul className="dropdown-menu downloaddropdown">
              <li>
                <button className="dropdown-item d-flex align-items-center gap-2" onClick={exportSummaryToExcel}>
                  <i className="bi bi-file-earmark-excel"></i> Download Excel
                </button>
              </li>
              <li>
                <button className="dropdown-item d-flex align-items-center gap-2" onClick={exportAsPDF}>
                  <i className="bi bi-file-earmark-pdf"></i> Export PDF
                </button>
              </li>
              <li>
                <button className="dropdown-item d-flex align-items-center gap-2" onClick={exportAsImage}>
                  <i className="bi bi-image"></i> Download Image
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Chart + Key Points */}
      <div className="d-flex align-items-center gap-3">
        {loading ? (
          <div className="loader-container"><div className="loader"></div></div>
        ) : (
          <Chart ref={chartRef} options={chartOptions} series={seriesData} type="donut" height={250} width={250} />
        )}
        <div className="key-points">
          <h6 className="fw-semibold mb-1">Key Points</h6>
          <p>Total Employees = {totalEmployees}</p>
          <p>Completion Rate = {completionPercentage}% ({completedEmployees} Participants)</p>
        </div>
      </div>

      {/* Remark Button */}
      <button
        title="Add Comment"
        onClick={() => {
          setShowRemarkModal(true);
          setTimeout(() => {
            if (modalRef.current) modalRef.current.click();
          }, 0);
        }}
        className="border-0 bg-transparent position-absolute"
        style={{
          bottom: "10px",
          right: "10px",

        }}
      >
        {/* <i className="bi bi-chat-dots"></i> */}
        <img src={CHAT_ICON} alt="chat" className="" />

      </button>

      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Remark" buttonLable="">
          <Remark
            selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remarkText}
            remarkId={remarkId}
            isAttempt={0}
            setRemarkprops={setRemarkText}
            setRemarkIdProps={setRemarkId}
            onCloseModal={() => setShowRemarkModal(false)}
          />
        </Modal>
      )}

      {/* Zoom Modal */}
      <ZoomModal
        id={modalId}
        title={title}
        chartOptions={chartOptions}
        seriesData={seriesData}
        type="donut"
        height={300}
        remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportSummaryToExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
      />
    </div>
  );
};

export default DonutChartWithToolbar;
